/**
 * User preferences — reactive $state proxy backed by localStorage.
 *
 * Preferences persist across sessions/refreshes. Access as `preferences.foo`;
 * assignments auto-persist via a $effect.root running once at module load.
 *
 * Namespaced under `pref:v1:*` so we can bump the version and invalidate
 * cleanly if we ever change the shape.
 *
 * Values here are UI preferences only — never store auth, PII, or content.
 * Storage is per-origin (per user session tab-shared) and survives logout.
 */

const NS = 'pref:v1'
const HAS_STORAGE = typeof window !== 'undefined' && !!window.localStorage

function readAll() {
  if (!HAS_STORAGE) return {}
  try {
    const raw = localStorage.getItem(NS)
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

function writeAll(next) {
  if (!HAS_STORAGE) return
  try { localStorage.setItem(NS, JSON.stringify(next)) } catch { /* quota / private mode — ignore */ }
}

const initial = readAll()

export const preferences = $state({
  /** Last year the user was actively viewing (StudyYear.order). null = never set. */
  currentYear: initial.currentYear ?? null,
  /** Question content language used while signed out / before session hydration. */
  contentLanguage: initial.contentLanguage === 'en' ? 'en' : 'fr',
})

// Persist on any mutation. $effect.root keeps subscription alive for app lifetime.
if (HAS_STORAGE) {
  $effect.root(() => {
    $effect(() => {
      writeAll({ currentYear: preferences.currentYear, contentLanguage: preferences.contentLanguage })
    })
  })
}

/** Reset all preferences. Call on logout if you want a clean slate. */
export function clearPreferences() {
  preferences.currentYear = null
  preferences.contentLanguage = 'fr'
  if (HAS_STORAGE) {
    try { localStorage.removeItem(NS) } catch { /* ignore */ }
  }
}
